import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import api from '../../services/api';
import { formatDate, extractListData } from '../../utils/Utils';

// ═══════ KARAR VEKÂLETİ — PLATFORM YÜZEYİNİN VERİ KATMANI ═══════
//
// Faz J'de bu gövde `performance-settings/config`'in içindeydi ve yalnız Performans
// Ayarları sekmesine hizmet ediyordu. Vekâlet platform seviyesine çıkınca (uç
// `v1/delegations`, kapsam kolonu) veri katmanı da modülden BAĞIMSIZ bir yere taşındı:
// Performans'a abone OLMAYAN bir kiracı da teklif onayı vekâleti yönetebilmeli.

// Sunucudaki DelegationScopes ile BİREBİR aynı olmalı. Yeni bir karar yüzeyi bağlandığında
// iki liste birlikte büyür; burada olmayan bir kapsam arayüzde seçilemez.
export const DELEGATION_SCOPES = ['performance', 'ats-offer'];

// Kapsam anahtarları tirelidir ("ats-offer"), çeviri anahtarları ise camelCase.
// Tireli bir kapsamı doğrudan çeviri anahtarı yapmak İKİ kapıyı birden bozardı:
// i18next anahtar ağacında tire ayrı bir düğüm gibi okunmaz ama denetim betiği
// dinamik/şablon anahtarı zaten göremez. Eşleme AÇIK yazılır ki her iki taraf da
// düz metin görsün.
//
// DİKKAT: bu yoruma ÖRNEK bir çeviri çağrısı YAZMAYIN — denetim betiği yorumları
// ayırt etmez, yorumdaki örneği gerçek bir anahtar sanar ve beş dilde birden
// "eksik anahtar" verir (bu dosyada bir kez oldu).
export const DELEGATION_SCOPE_LABEL_KEYS = {
  performance: 'delegations.scopes.performance',
  'ats-offer': 'delegations.scopes.atsOffer',
};

// comp-cycles/config'teki StatusBadge ile AYNI imza (color/bg düz prop).
export const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

// "Durum" kolonu BE'nin IsActive'ini DEĞİL, pencerenin BUGÜNE göre türetilmiş durumunu
// gösterir — GET zaten yalnız aktif kayıtları döndürüyor, bu yüzden IsActive her satırda
// hep true olurdu (anlamsız kolon). startDate/endDate saat bileşeni taşımaz, gün-düzeyinde
// karşılaştırma yeterli.
export const DELEGATION_WINDOW_COLORS = {
  upcoming: { color: '#0291EF', bg: '#EFF6FF' },
  ongoing: { color: '#16A34A', bg: '#DCFCE7' },
  expired: { color: '#64748B', bg: '#F1F5F9' },
};

export function computeDelegationWindowStatus(startDate, endDate, now = new Date()) {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const start = startDate ? new Date(startDate) : null;
  const end = endDate ? new Date(endDate) : null;
  if (start && today < new Date(start.getFullYear(), start.getMonth(), start.getDate())) return 'upcoming';
  if (end && today > new Date(end.getFullYear(), end.getMonth(), end.getDate())) return 'expired';
  return 'ongoing';
}

// /v1/employees ucu — nine-box/ProcessStartDrawer ve performance-settings/config'teki
// buildEmployeeAsync'in aynısı (bu depoda üçüncü kopya; kopyalar bilinçli, çünkü her
// tüketici kendi alan eşlemesini yapıyor ve modüller birbirine bağlanmıyor).
export function buildEmployeeAsync() {
  return {
    onSearch: async (q) => {
      const params = q ? { 'fullName[like]': q, pageSize: 20 } : { pageSize: 20 };
      const res = await api.get('/v1/employees', { params: { ...params, lookup: true } });
      const items = extractListData(res);
      return items.map((e) => ({
        id: e.id,
        name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(),
        profilePicture: e.profilePicture,
      }));
    },
    onGetById: async (id) => {
      if (!id) return null;
      const res = await api.get(`/v1/employees/${id}`);
      const e = res.data?.data;
      return e
        ? { id: e.id, name: e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim(), profilePicture: e.profilePicture }
        : null;
    },
  };
}

export const employeeAsync = buildEmployeeAsync();

// GET v1/delegations SAYFALI. Yalnız oluşturma + pasifleştirme; güncelleme (PUT) BE'de
// var ama bu yüzeyde kullanılmıyor (vekâlet penceresi düzeltilecekse kapatılıp yenisi
// kurulur — iki adımın da denetim izi ayrı ayrı yazılır).
//
// `lockedScope` verildiğinde hem liste SÜZÜLÜR hem yeni kayıt O KAPSAMDA açılır: Performans
// Ayarları sekmesi kendi kapsamının dışına ne bakar ne yazar.
export const useDelegationsApi = (queryParams, lockedScope = null) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const base = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : { pageNumber: 1, pageSize: 10 };
    const params = lockedScope ? { ...base, 'scope[eq]': lockedScope } : base;
    setFetching(true);
    api.get(`/v1/delegations?${new URLSearchParams(params)}`)
      .then((res) => {
        // ═══ "YÜKLENEMEDİ" ile "VEKÂLET YOK" AYNI ŞEY DEĞİL ═══
        // api.js GET okumalarında reject ETMEZ: sunucu hata döndürse bile `then` dalına
        // düşülür ve `res.data.data` undefined olur. Bunu boş listeye indirgemek, İK'ya
        // "bu kiracıda hiç vekâlet yok" diye YANLIŞ bir gerçek gösterirdi — üstelik tam da
        // birinin yetkisini sorguladığı anda. Gerçek sinyal `res.data.status`tır.
        const ok = res?.data?.status === true;
        const result = res?.data?.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setLoadFailed(!ok);
        setData(ok ? items : []);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger, lockedScope]);

  const createDelegation = useCallback(async (payload) => {
    try {
      await api.post('/v1/delegations', payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const deactivateDelegation = useCallback(async (id) => {
    try {
      await api.delete(`/v1/delegations/${id}`);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    const scopeColumn = {
      name: 'scope',
      label: t('delegations.columns.scope'),
      options: {
        filter: false,
        sort: false,
        // BİLİNEN kapsam çevrilir; BİLİNMEYEN bir değer ham hâliyle gösterilir —
        // sunucu yeni bir kapsam eklediğinde arayüz "delegations.scopes.unknown"
        // diye anlamsız bir etiket değil, gerçek değeri gösterir.
        customBodyRender: (value) => {
          const key = DELEGATION_SCOPE_LABEL_KEYS[value];
          return key ? t(key) : (value || '-');
        },
      },
    };

    return [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      // Kapsam kolonu YALNIZ kapsam sabitlenmemişken anlamlıdır: tek kapsamlı listede
      // her satırda aynı değeri tekrar etmek bilgi taşımaz.
      ...(lockedScope ? [] : [scopeColumn]),
      {
        name: 'delegatorName',
        label: t('delegations.columns.delegator'),
        options: { filter: false, sort: false },
      },
      {
        name: 'delegateName',
        label: t('delegations.columns.delegate'),
        options: { filter: false, sort: false },
      },
      {
        name: 'window',
        label: t('delegations.columns.window'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return '-';
            return `${formatDate(row.startDate, lang)} — ${formatDate(row.endDate, lang)}`;
          },
        },
      },
      {
        name: 'status',
        label: t('delegations.columns.status'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            if (!row) return '-';
            const status = computeDelegationWindowStatus(row.startDate, row.endDate);
            const cfg = DELEGATION_WINDOW_COLORS[status];
            return (
              <Badge data-role="delegation-status-badge" color={cfg.color} bg={cfg.bg}>
                {t(`delegations.status.${status}`, { defaultValue: status })}
              </Badge>
            );
          },
        },
      },
    ];
  }, [t, lang, data, lockedScope]);

  return {
    fetching, data, loadFailed, pagination, columns, createDelegation, deactivateDelegation, refresh,
  };
};
